import { Stack, Typography, useTheme } from "@mui/material";
import { Post } from "src/api/client-api/post/postSchema";
import { SsqSsqHeading, SsqSsqHeadingSsqProperty } from "src/api/sure-api/ssq/useGetOneSsqVersion";
import { FormAccordion, FormUnit } from "@foodpilot/foods";
import { PropertyContainer } from "./PropertyContainer";
import { getVisibleSsqSsqHeadingSsqProperties } from "src/utils/ssq";
import { PropertyUpdateHandler } from "./FormProperty/context/context";
import { DeleteProperty } from "src/api/client-api/property/useDeleteProperty";
import { getDisplayValueForScore } from "src/utils/scores";
import { HeadingHelperTextBox } from "./HeadingHelperTextBox";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import { ValidationMessagesByKey } from "src/components/QuestionnaireForm/FormProperty/validationTypes";
import { DataListValueOverride } from "src/api/client-api/dataListValueOverride/useGetAllDataListValueOverride";
import { UnifiedItem as UnifiedItemType } from "./context/utils/unifyHeadingsAndProperties";
import { Dimension } from "src/api/client-api/olapScores/olapScoresSchema";

type UnifiedItemProps = {
    item: UnifiedItemType;
    ssqVersionStructureId?: number | null;
    post?: Post;
    changePost: (callable: (post: Post) => void) => void;
    onPropertyUpdate: PropertyUpdateHandler;
    onPropertyDelete: (data: DeleteProperty) => void;
    setPost: (post: Post) => void;
    scoreDimensions?: {
        subheadings: Dimension | undefined;
        properties: Dimension | undefined;
    };
    gradeDimensions?: {
        subheadings: Dimension | undefined;
        properties: Dimension | undefined;
    };
    precision: number;
    unit: string;
    validationErrors: ValidationMessagesByKey;
    dataListValueOverrides?: DataListValueOverride[];
    propertyRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
};

export const UnifiedItem = (props: UnifiedItemProps) => {
    const { item } = props;

    if (item.type === "subheading") {
        return <SubHeadingItem {...props} subheading={item.data} />;
    }

    return <PropertyItem {...props} ssqSsqHeadingSsqProperty={item.data} />;
};

const SubHeadingItem = (props: UnifiedItemProps & { subheading: SsqSsqHeading }) => {
    const {
        subheading,
        post,
        scoreDimensions,
        gradeDimensions,
        precision,
        unit,
        propertyRefs,
        onPropertyUpdate,
        onPropertyDelete,
        changePost,
        setPost,
        validationErrors,
        dataListValueOverrides,
    } = props;
    const theme = useTheme();

    const subheadingProperties = post ? getVisibleSsqSsqHeadingSsqProperties(subheading, post.hiddenElements) : [];

    const subheadingScoresValue = OlapScoresService.getDimensionValue(subheading.id, scoreDimensions?.subheadings);
    const subheadingSubscoresValue = OlapScoresService.getDimensionValue(subheading.id, gradeDimensions?.subheadings);

    const subheadingScore = subheadingScoresValue?.score.value;
    const subheadingScoreValue = OlapScoresService.normalizeNumericalScore(subheadingScore);

    const subheadingPropertiesScoresDimension = OlapScoresService.getDimension(
        "properties",
        subheadingScoresValue?.dimensions,
    );
    const subheadingPropertiesGradeDimension = OlapScoresService.getDimension(
        "properties",
        subheadingSubscoresValue?.dimensions,
    );

    const scoreValue = subheadingScoreValue !== null ? getDisplayValueForScore(subheadingScoreValue, precision) : null;

    return (
        <FormAccordion
            key={`subheading-${subheading.id}`}
            AccordionDetailSx={{
                padding: { xs: theme.spacing(2), sm: theme.spacing(2) },
            }}
            pages={[
                {
                    renderTitle: () => {
                        return (
                            <Stack
                                paddingInline={theme.spacing(2)}
                                width={"100%"}
                                justifyContent={"space-between"}
                                gap={theme.spacing(1)}
                                flexDirection={{ xs: "column", sm: "row" }}
                                alignItems={{ xs: "start", sm: "center" }}
                            >
                                <Typography variant={"h3"}>{subheading.ssqHeading.title}</Typography>
                                {scoreValue !== null && <FormUnit value={scoreValue} unit={unit} />}
                            </Stack>
                        );
                    },
                    content: (
                        <Stack gap={{ xs: theme.spacing(1), sm: theme.spacing(2) }}>
                            <HeadingHelperTextBox value={subheading.helperText} />

                            {subheadingProperties.map((ssqSsqHeadingSsqProperty: SsqSsqHeadingSsqProperty) => {
                                const propertyScoresValue = OlapScoresService.getPropertyDimensionValue(
                                    ssqSsqHeadingSsqProperty.ssqProperty.id,
                                    subheading.ssqVersionStructureId,
                                    subheadingPropertiesScoresDimension,
                                );
                                const propertyGradeValue = OlapScoresService.getPropertyDimensionValue(
                                    ssqSsqHeadingSsqProperty.ssqProperty.id,
                                    subheading.ssqVersionStructureId,
                                    subheadingPropertiesGradeDimension,
                                );

                                return (
                                    <div
                                        key={ssqSsqHeadingSsqProperty.id}
                                        ref={(el) => {
                                            propertyRefs.current[ssqSsqHeadingSsqProperty.ssqProperty.id] = el;
                                        }}
                                    >
                                        <PropertyContainer
                                            ssqSsqHeadingSsqProperty={ssqSsqHeadingSsqProperty}
                                            ssqVersionStructureId={subheading.ssqVersionStructureId}
                                            onUpdate={onPropertyUpdate}
                                            onDelete={onPropertyDelete}
                                            changePost={() => {
                                                changePost((p) => setPost(p));
                                            }}
                                            dimensionValue={propertyScoresValue}
                                            gradeDimensionValue={propertyGradeValue}
                                            scoreUnit={unit}
                                            validationErrors={validationErrors}
                                            dataListValueOverrides={dataListValueOverrides}
                                        />
                                    </div>
                                );
                            })}
                        </Stack>
                    ),
                },
            ]}
        />
    );
};

const PropertyItem = (props: UnifiedItemProps & { ssqSsqHeadingSsqProperty: SsqSsqHeadingSsqProperty }) => {
    const {
        ssqSsqHeadingSsqProperty,
        ssqVersionStructureId,
        scoreDimensions,
        gradeDimensions,
        propertyRefs,
        onPropertyUpdate,
        onPropertyDelete,
        changePost,
        setPost,
        unit,
        validationErrors,
        dataListValueOverrides,
    } = props;

    const propertyScoresValue = OlapScoresService.getPropertyDimensionValue(
        ssqSsqHeadingSsqProperty.ssqProperty.id,
        ssqVersionStructureId,
        scoreDimensions?.properties,
    );
    const propertyGradeValue = OlapScoresService.getPropertyDimensionValue(
        ssqSsqHeadingSsqProperty.ssqProperty.id,
        ssqVersionStructureId,
        gradeDimensions?.properties,
    );

    return (
        <div
            key={`property-${ssqSsqHeadingSsqProperty.id}`}
            ref={(el) => {
                propertyRefs.current[ssqSsqHeadingSsqProperty.ssqProperty.id] = el;
            }}
        >
            <PropertyContainer
                ssqSsqHeadingSsqProperty={ssqSsqHeadingSsqProperty}
                ssqVersionStructureId={ssqVersionStructureId}
                onUpdate={onPropertyUpdate}
                onDelete={onPropertyDelete}
                changePost={() => changePost((p) => setPost(p))}
                dimensionValue={propertyScoresValue}
                gradeDimensionValue={propertyGradeValue}
                scoreUnit={unit}
                validationErrors={validationErrors}
                dataListValueOverrides={dataListValueOverrides}
            />
        </div>
    );
};
